<template>
  <div class="product-form">
    <product-sub-navigation
      page-type="pdp"
      :price="price || liquid.price"
      :product="product"
      :variant="variant"
      :state="state"
      :sticky-position="stickyPosition"
      @buy-clicked="handleAddToCart"
    />

    <form
      class="product-form__form"
      js-product-form="container"
      js-sub-navigation="scrollTrigger"
      @submit.prevent="handleAddToCart"
    >
      <div class="form-group">
        <div
          v-if="liquid.skuEnable && variant?.sku"
          class="product-form__sku"
        >
          <div
            class="product-form__sku-label text-heading-l"
            v-text="$string('product.form.sku_label')"
          />

          <div
            class="product-form__sku-value text-heading-l"
            v-text="variant?.sku"
          />
        </div>

        <div
          v-show="!hasOnlyDefaultVariant"
          class="product-form__swatch-container"
        >
          <product-swatches
            v-for="(option) of options"
            :key="`${option.name}-${option.index}`"
            :option="option"
            :options="options"
            :product="product"
            :selected-options="selectedOptions"
            @option-change="handleOptionChange"
          />
        </div>

        <teaser-button
          v-if="modelTeasers.length"
          :filtered-teasers="modelTeasers"
        />

        <product-addons
          :product="product"
          :variant="variant"
          tabindex="0"
          @update-addon="updateAddOn"
        />

        <teaser-button
          v-if="addonTeasers.length"
          :filtered-teasers="addonTeasers"
        />

        <div
          v-if="state !== 'b2b blocked' && !disablePurchasing"
          class="form-group__footer grid grid--col-m4"
        >
          <div class="product-form__quantity-selector-container col xs1 s1 m1">
            <quantity-selector
              block-class="product-form"
              :state="state"
              :quantity="quantity"
              :allow-zero="false"
              large
              @update-quantity="handleQuantityUpdate"
            />
          </div>

          <div class="col xs4 s3 m3">
            <btn
              class="main-product__add-to-cart"
              js-product-form="add-to-cart"
              block-class="main-product"
              :label="addToCartText"
              :modifiers="ctaModifiers"
              :show-icon="false"
              :show-state-icon="state === 'adding'"
              type="submit"
              :disabled="addOnProduct || state === 'out of stock'"
            >
              <template #icon>
                <component :is="addToCartIcon" />
              </template>

              <template #stateIcon>
                <icon-loading />
              </template>
            </btn>

            <p
              v-if="shippingText"
              class="product-form__shipping text-body-xs"
              v-html="shippingText"
            />
          </div>
        </div>

        <p
          v-if="state === 'b2b blocked' || disablePurchasing"
          class="product-form__blocked-variant-note text-body-xs"
          v-text="$string('product.form.b2b_blocked_variant')"
        />
      </div>
    </form>

    <p
      v-if="error"
      class="product-form__error text-body-m"
      v-text="error"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {Boolean} liquid.skuEnable - Theme settings to enable Sku.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} modelTeaserData - Metaobject model teaser data.
 * @param {Object} addonTeaserData - Metaobject addon teaser data.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ProductAddons from '~async/product-addons/product-addons'
import ProductSubNavigation from '~async/product-sub-navigation/product-sub-navigation'
import ProductSwatches from '~async/product-swatches/product-swatches'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import TeaserButton from '~async/teaser-button/teaser-button'

import IconArrowRight from '~icons/direction/arrow/right.svg'
import IconLoading from '~icons/misc/loading.svg'
import IconTick from '~icons/misc/tick.svg'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    IconLoading,
    ProductAddons,
    ProductSubNavigation,
    ProductSwatches,
    QuantitySelector,
    TeaserButton,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        hasOnlyDefaultVariant: false,
        options: [],
        skuEnable: false,
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    addons: {
      type: Array,
      default: () => [],
    },
    modelTeaserData: {
      type: Object,
      default: () => ({}),
    },
    addonTeaserData: {
      type: Object,
      default: () => ({}),
    },
    blockedVariantsList: {
      type: Array,
      default: () => [],
    },
    outOfStockVariantsList: {
      type: Array,
      default: () => [],
    },
    hiddenVariantsList: {
      type: Array,
      default: () => [],
    },
    stickyPosition: {
      type: String,
      default: 'top',
    },
    price: {
      type: [Boolean, Number],
      default: 0,
    },
    disablePurchasing: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-variant': () => true,
    'update-addon': () => true,
    'clear-addons': () => true,
  },

  data() {
    return {
      currentVariantId: this.variant?.id || 0,
      error: '',
      lineItemProperties: {
        _groupId: false,
      },
      quantity: 1,
      state: 'ready',
      timeout: {},
      selectedOptions: this.liquid?.selectedOptions || [],
      addOnProduct: this.liquid.addOnProduct,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart icon.
     * @returns {String}
     */
    addToCartIcon() {
      return this.state === 'added'
        ? IconTick
        : IconArrowRight
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (this.state === 'out of stock') {
        return this.$string('product.form.sold_out')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute CTA modifiers.
     * @returns {String}
     */
    ctaModifiers() {
      return this.state === 'out of stock'
        ? 'large block'
        : 'dark large block'
    },

    /**
     * Compute shipping line item property.
     * @returns {String}
     */

    shippingProperty() {
      if (!this.variant) {
        return ''
      }

      if (this.variant.inventory < 1 && this.variant.outOfStockDays) {
        return this.$string(
          'product.form.shipping_property_value',
          { replace: { days: this.variant.outOfStockDays } },
        )
      }

      if (this.variant.inStockDays) {
        return this.$string(
          'product.form.shipping_property_value',
          { replace: { days: this.variant.inStockDays } },
        )
      }

      return ''
    },

    /**
     * Compute shipping text.
     * @returns {String}
     */
    shippingText() {
      if (!this.variant) {
        return ''
      }

      if (this.variant.inventory < 1 && this.variant.outOfStockDays) {
        let shippingText = this.$string(
          'product.form.expect_shipping',
          { replace: { days: this.variant.outOfStockDays } },
        )
        if (this.addons.length) {
          shippingText += `<br/>${this.$string('product.form.expect_shipping_addons')}`
        }

        return shippingText
      }

      if (this.variant.inStockDays) {
        let shippingText = this.$string(
          'product.form.expect_shipping',
          { replace: { days: this.variant.inStockDays } },
        )
        if (this.addons.length) {
          shippingText += `<br/>${this.$string('product.form.expect_shipping_addons')}`
        }

        return shippingText
      }

      return ''
    },

    /**
     * Compute has only default variant from Liquid and product objects.
     * @returns {Boolean}
     */
    hasOnlyDefaultVariant() {
      return this.liquid.hasOnlyDefaultVariant || this.product.hasOnlyDefaultVariant
    },

    /**
     * Computed options.
     * - Fallback to liquid object.
     */
    options() {
      if (!this.product || !this.variant) {
        return []
      }

      const allOptions = this.product.options || this.liquid?.options

      const visibleVariants = this.product.variants
        .filter(variant => !this.hiddenVariantsList.includes(variant.id))

      const filteredAllOptions = allOptions.map((option, optionIndex) => {
        return {
          ...option,
          values: option.values.map(optionValue => {
            const [
              selectedOption1,
              selectedOption2,
              selectedOption3,
            ] = this.selectedOptions

            const otherOptionsAreMatching = visibleVariants.some(variant => {
              return variant.selectedOptions.every((selectedOption, selectedOptionIndex) => {
                if (selectedOptionIndex === optionIndex) {
                  return selectedOption.value === optionValue
                }

                if (selectedOptionIndex === 0 && optionIndex !== 0) {
                  return selectedOption.value === selectedOption1.value
                }

                if (selectedOptionIndex === 1 && optionIndex !== 1) {
                  return selectedOption.value === selectedOption2.value
                }

                if (selectedOptionIndex === 2 && optionIndex !== 2) {
                  return selectedOption.value === selectedOption3.value
                }

                return true
              })
            })
            return otherOptionsAreMatching ? optionValue : null
          }).filter(Boolean),
        }
      })

      return filteredAllOptions
    },

    /**
     * Model teasers.
     * @returns {Array} - Output matching metaobjects where handle equals
     * product tags string.
     */
    modelTeasers() {
      const metaObjectData = []

      this.product.tags?.forEach((tag) => {
        if (tag.includes('model-teaser:')) {
          const splitTag = tag.split(':')
          const tagText = splitTag[1].trim()

          const data = this.modelTeaserData.find((modelTeaserData) => {
            return modelTeaserData.handle === tagText
          })

          metaObjectData.push(data)
        }
      })

      const filtered = metaObjectData.filter((data) => {
        // eslint-disable-next-line no-undefined
        return data !== undefined
      })

      return filtered
    },

    /**
     * Addon teasers.
     * @returns {Array} - Output matching metaobjects where handle equals
     * product tags string.
     */
    addonTeasers() {
      const metaObjectData = []

      this.product.tags?.forEach((tag) => {
        if (tag.includes('addon-teaser:')) {
          const splitTag = tag.split(':')
          const tagText = splitTag[1].trim()

          const data = this.addonTeaserData.find((addonTeaserData) => {
            return addonTeaserData.handle === tagText
          })

          metaObjectData.push(data)
        }
      })

      const filtered = metaObjectData.filter((data) => {
        // eslint-disable-next-line no-undefined
        return data !== undefined
      })

      return filtered
    },
  },

  watch: {

    /**
     * Watch for variant change and handle update
     * - If there is a current variant set - update the `selectedOptions` data
     *   to match the new variant's `selectedOptions`
     * - If there are selected options set, but the new variant is set to
     *   `false` it means the options combination does not match a variant so
     *   the last selected option is reset to false.
     */
    variant() {
      this.error = ''
      this.showSwatchError = false
      this.$emit('clear-addons')

      if (this.variant) {
        if (this.blockedVariantsList.includes(this.variant.id) || this.disablePurchasing) {
          this.state = 'b2b blocked'
          return
        }

        const selectedOptions = [...this.variant.selectedOptions]
        this.selectedOptions = selectedOptions
        this.state = this.variant.available && !this.outOfStockVariantsList.includes(this.variant.id) ? 'ready' : 'out of stock'
      } else if (this.selectedOptions.length) {
        this.selectedOptions[this.lastChangedOptionIndex] = false
      }
    },

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromId(this.currentVariantId)

      if (!variant) {
        return
      }

      this.$emit('update-variant', variant)
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      this.getCurrentVariant()
      this.quantity = 1
    },
  },

  mounted() {
    this.getCurrentVariant()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
      setProductInfoAdded: 'cart/setProductInfoAdded',
    }),

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      if (this.hasOnlyDefaultVariant) {
        this.currentVariantId = this.product.variants[0].id
        return
      }

      if (this.variant && !this.hiddenVariantsList.includes(this.variant.id)) {
        this.selectedOptions = [...this.variant.selectedOptions]
        return
      }

      /**
       * Pick the first in stock variant.
       * - If no variants are in stock use first.
       * - Or use variant from URL if set.
       */
      const inStockVariants = this.product.variants.filter((variant) => {
        return variant.available && !this.hiddenVariantsList.includes(variant.id)
      })

      const defaultVariant = inStockVariants.length
        ? inStockVariants[0]
        : this.product.variants[0]

      const variant = this.getVariantFromUrl() || defaultVariant

      this.currentVariantId = variant.id
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))

      if (!this.hiddenVariantsList.includes(variantId)) {
        return this.getVariantFromId(variantId)
      }

      return null
    },

    /**
     * Get variant object from variant ID.
     * @returns {Object}
     */
    getVariantFromId(variantId) {
      return this.product.variants.find((variant) => {
        return variant.id === variantId
      })
    },

    /**
     * Get variant title.
     * @param {Object} variant - Variant object.
     * @returns {String}
     */
    getVariantTitle(variant) {
      return variant.available
        ? variant.title
        : `${variant.title} - ${this.$string('product.form.sold_out')}`
    },

    /**
     * Handle quantity selector update.
     * @param {String} quantity - New quantity.
     */
    handleQuantityUpdate(quantity) {
      this.quantity = quantity
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (cnvs.storybook) {
        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      const lineItemProperties = {
        ...this.lineItemProperties,
      }

      if (this.shippingProperty) {
        lineItemProperties[this.$string('product.form.shipping_property_key')] = this.shippingProperty
      }

      /**
       * If there are items in the addons array, then we override the `groupId`
       * line item property with a timestamp.
       */
      if (this.addons.length) {
        // eslint-disable-next-line no-underscore-dangle
        lineItemProperties._groupId = Date.now()
      }

      if (cnvs.settings.skuEnabled) {
        // eslint-disable-next-line no-underscore-dangle
        lineItemProperties._sku = this.variant?.sku
      }

      const items = [
        ...this.addons.map((addon) => {
          return {
            id: addon.id,
            properties: {
              ...lineItemProperties,
              _addon: true,
            },
            quantity: this.quantity,
          }
        }),
        {
          id: this.variant.id,
          properties: lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        if (this.getComponentExists('cart-drawer')) {
          this.setProductInfoAdded(true)

          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

      } catch (error) {
        this.state = 'error'

        if (error.length) {
          this.error = error
          return
        }

        reportError('Failed to add product to cart', error)
      }
    },

    /**
     * Handle option change from product swatch
     * - Updates the `selectedOptions` object,
     * - Finds a matching variant from the new options object
     * - Emits a variant change
     * @param {Number} index - Index of option that has changed
     * @param {Object} data - Option data
     * @param {String} data.name - Option name
     * @param {String} data.value - Option value
     */
    handleOptionChange(index, data) {
      this.lastChangedOptionIndex = index
      this.selectedOptions[index] = data

      const newlySelectedVariant = this.product.variants.find((variant) => {
        const variantOptionString = JSON.stringify(variant.selectedOptions)
        const newOptionString = JSON.stringify(this.selectedOptions)

        return variantOptionString === newOptionString &&
          !this.hiddenVariantsList.includes(variant.id)
      })

      const closestVariantToNewlySelectedOption = this.product.variants.find((variant) => {
        if (data && data.value) {

          return variant.selectedOptions.some((option) => {
            return option.value === data.value
          })
        }

        return null
      })

      const closestVariantToSelectedOptions = this.product.variants.find((variant) => {
        return variant.selectedOptions.some((option, optionIndex) => {
          return option.value === this.selectedOptions[optionIndex].value
        })
      })

      const newVariant = newlySelectedVariant ||
        closestVariantToNewlySelectedOption ||
        closestVariantToSelectedOptions

      if (newVariant) {
        this.$emit('update-variant', newVariant)

        if (!newVariant.available || this.outOfStockVariantsList.includes(newVariant.id)) {
          this.state = 'out of stock'
        } else if (this.variant?.id === newVariant.id) {
          this.state = 'ready'
        }
      } else {
        this.state = 'out of stock'
      }
    },

    /**
     * update addon
     * @param {Object} addon - addon data
     */
    updateAddOn(addon) {
      this.$emit('update-addon', addon)
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
